import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Table, Input, Dropdown } from 'semantic-ui-react';
import { useData } from '../context/DataContext';
import ShareLinkButton from '../components/ShareLinkButton';

const searchModeOptions = [
  { key: 'club', text: 'Rechercher un club', value: 'club' },
  { key: 'person', text: 'Rechercher une personne', value: 'person' },
];

const SearchPage = () => {
  const { data, isLoading, error } = useData();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filteredClubs, setFilteredClubs] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClub, setSelectedClub] = useState(null);
  const [searchMode, setSearchMode] = useState('club');

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [membersPerPage] = useState(10);

  // Comparateur de patineurs
  const [compareList, setCompareList] = useState([]);
  const compareResultsRef = useRef(null);

  const clubResultsRef = useRef(null);
  const personResultsRef = useRef(null);
  const hasRestoredRef = useRef(false);

  // Réinitialise la recherche en cours si les données changent (nouvelle saison/genre)
  useEffect(() => {
    setFilteredClubs([]);
    setFilteredMembers([]);
    setSelectedClub(null);
    setSelectedPerson(null);
    setSearchTerm('');
    setCompareList([]);

    // Si on avait déjà restauré une sélection depuis l'URL, un changement de saison/genre
    // rend cette sélection potentiellement obsolète : on nettoie l'URL.
    if (hasRestoredRef.current) {
      const params = new URLSearchParams(searchParams);
      params.delete('club');
      params.delete('nom');
      params.delete('prenom');
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Restaure club/personne depuis l'URL une fois les données chargées (une seule fois)
  useEffect(() => {
    if (hasRestoredRef.current) return;
    if (data.length === 0) return;

    const clubParam = searchParams.get('club');
    const nomParam = searchParams.get('nom');
    const prenomParam = searchParams.get('prenom');

    if (clubParam) {
      setSelectedClub(clubParam);
      setSearchTerm(clubParam);
    } else if (nomParam && prenomParam) {
      const found = data.find(item => item['Nom'] === nomParam && item['Prénom'] === prenomParam);
      if (found) {
        setSelectedPerson(found);
        setSearchTerm(`${found['Nom']} ${found['Prénom']}`);
      }
    }

    hasRestoredRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Scroll automatique
  useEffect(() => {
    if (selectedClub && clubResultsRef.current) {
      clubResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedClub]);

  useEffect(() => {
    if (selectedPerson && personResultsRef.current) {
      personResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedPerson]);

  useEffect(() => {
    if (compareList.length > 0 && compareResultsRef.current) {
      compareResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [compareList.length]);

  const handleSearchChange = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    setFilteredClubs([]);
    setFilteredMembers([]);
    setSelectedClub(null);
    setSelectedPerson(null);

    if (!term) return;

    if (searchMode === 'club') {
      const clubs = [...new Set(
        data.filter(item => item['Club']?.toLowerCase().includes(term))
          .map(item => item['Club'])
      )];
      setFilteredClubs(clubs);
    } else {
      const members = data.filter(item =>
        item['Prénom']?.toLowerCase().includes(term) ||
        item['Nom']?.toLowerCase().includes(term)
      );
      setFilteredMembers(members);
    }
  };

  const handleSearchModeChange = (e, { value }) => {
    setSearchMode(value);
    setSearchTerm('');
    setFilteredClubs([]);
    setFilteredMembers([]);
    setSelectedClub(null);
    setSelectedPerson(null);
  };

  const handleClubSelect = (club) => {
    setSelectedClub(club);
    setSelectedPerson(null);
    setFilteredClubs([]);
    setSearchTerm(club);

    const params = new URLSearchParams(searchParams);
    params.set('club', club);
    params.delete('nom');
    params.delete('prenom');
    setSearchParams(params, { replace: true });
  };

  const handlePersonSelect = (person) => {
    setSelectedPerson(person);
    setSelectedClub(null);
    setFilteredMembers([]);
    setSearchTerm(`${person['Nom']} ${person['Prénom']}`);

    const params = new URLSearchParams(searchParams);
    params.set('nom', person['Nom']);
    params.set('prenom', person['Prénom']);
    params.delete('club');
    setSearchParams(params, { replace: true });
  };

  const handleCategoryClick = (cat) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
    setIsModalOpen(true);
  };

  const getCategoryMembers = (cat) =>
    data.filter(item => item['Catégorie'] === cat);

  const selectedClubMembers = data.filter(item => item['Club'] === selectedClub);

  const lastRank = data.length;

  const getCategoryRanking = (members) => {
    const total = members.length;
    return members.map((m, i) => ({
      ...m,
      CategoryRank: `${i + 1} sur ${total}`,
    }));
  };

  const paginatedMembers = (members) => {
    const start = (currentPage - 1) * membersPerPage;
    return members.slice(start, start + membersPerPage);
  };

  const totalPages = selectedCategory
    ? Math.ceil(getCategoryMembers(selectedCategory).length / membersPerPage)
    : 1;

  // ====================== COMPARATEUR ======================
  const getPersonKey = (person) => `${person['Nom']}_${person['Prénom']}_${person['Club']}`;

  const isInCompare = (person) =>
    compareList.some(p => getPersonKey(p) === getPersonKey(person));

  const addToCompare = (person, e) => {
    e.stopPropagation();
    if (isInCompare(person)) return;
    if (compareList.length >= 5) return;
    setCompareList(prev => [...prev, person]);
  };

  const removeFromCompare = (person) => {
    setCompareList(prev => prev.filter(p => getPersonKey(p) !== getPersonKey(person)));
  };

  const clearCompare = () => setCompareList([]);

  return (
    <div>
      {/* === HEADER === */}
      <div className="premium-header text-center py-5">
        <div className="container">
          <div className="mb-4">
            <img
              src={require('../assets/img/logo_front.png')}
              alt="French Ranking Logo"
              className="premium-logo"
            />
          </div>

          <h1 className="premium-title mb-5">Recherche d'un patineur ou un club</h1>

          <div className="col-12 col-lg-6 mx-auto">
            <Dropdown fluid selection options={searchModeOptions} value={searchMode} onChange={handleSearchModeChange} className="premium-dropdown mb-3" />

            <div className="search-wrapper mx-auto" style={{ maxWidth: '600px' }}>
              <Input
                icon="search"
                fluid
                placeholder={searchMode === 'club' ? 'Rechercher un club...' : 'Rechercher une personne...'}
                value={searchTerm}
                onChange={handleSearchChange}
                className="premium-search-input"
                disabled={isLoading}
              />

              {isLoading && (
                <div className="premium-loading text-muted mt-3">Chargement des données...</div>
              )}

              {error && (
                <div className="premium-error mt-3">{error}</div>
              )}

              {(filteredClubs.length > 0 || filteredMembers.length > 0) && searchTerm && (
                <div className="search-results">
                  {searchMode === 'club' &&
                    filteredClubs.map((club, i) => (
                      <div key={i} className="result-item" onClick={() => handleClubSelect(club)}>
                        🏆 {club}
                      </div>
                    ))}

                  {searchMode === 'person' &&
                    filteredMembers.map((person, i) => (
                      <div key={i} className="result-item d-flex justify-content-between align-items-center">
                        <span onClick={() => handlePersonSelect(person)} style={{ cursor: 'pointer', flex: 1 }}>
                          👤 {person['Nom']} {person['Prénom']} — {person['Club']}
                        </span>
                        <button
                          type="button"
                          className={`btn btn-sm ms-2 ${isInCompare(person) ? 'btn-success' : 'btn-outline-primary'}`}
                          disabled={isInCompare(person) || compareList.length >= 5}
                          onClick={(e) => addToCompare(person, e)}
                          title="Ajouter au comparateur"
                        >
                          {isInCompare(person) ? '✓' : '+'}
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* === RÉSULTATS CLUB === */}
      {selectedClub && (
        <div ref={clubResultsRef} className="container mt-5">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <h3 className="premium-section-title mb-0">
              Membres du club : <span className="text-primary">{selectedClub}</span>
            </h3>
            <ShareLinkButton />
          </div>
          <Table celled compact className="premium-table">
            <Table.Header>
              <Table.Row className="text-center">
                <Table.HeaderCell>Nom Prénom</Table.HeaderCell>
                <Table.HeaderCell>Naissance</Table.HeaderCell>
                <Table.HeaderCell>Classement Général</Table.HeaderCell>
                <Table.HeaderCell>Médaille</Table.HeaderCell>
                <Table.HeaderCell>Catégorie</Table.HeaderCell>
                <Table.HeaderCell>Meilleur Score</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {selectedClubMembers.map((member, index) => (
                <Table.Row key={index} className="text-center">
                  <Table.Cell>{member['Nom']} {member['Prénom']}</Table.Cell>
                  <Table.Cell>{member['Naissance']}</Table.Cell>
                  <Table.Cell>{member['Cl. Gal']} ème sur {lastRank}</Table.Cell>
                  <Table.Cell>{member['Méd.']}</Table.Cell>
                  <Table.Cell
                    className="text-danger fw-bold"
                    onClick={() => handleCategoryClick(member['Catégorie'])}
                    style={{ cursor: 'pointer' }}
                  >
                    {member['Catégorie']}
                  </Table.Cell>
                  <Table.Cell>{member['Season Best'] || member['Score Best'] || '—'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {/* === RÉSULTATS PERSONNE === */}
      {selectedPerson && (
        <div ref={personResultsRef} className="container mt-5">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <h3 className="premium-section-title mb-0">
              Détails de : <span className="text-primary">{selectedPerson['Nom']} {selectedPerson['Prénom']}</span>
            </h3>
            <ShareLinkButton />
          </div>
          <Table celled compact className="premium-table mb-5">
            <Table.Body>
              <Table.Row>
                <Table.Cell><strong>Nom complet</strong></Table.Cell>
                <Table.Cell>{selectedPerson['Nom']} {selectedPerson['Prénom']}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Date de naissance</strong></Table.Cell>
                <Table.Cell>{selectedPerson['Naissance']}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Club</strong></Table.Cell>
                <Table.Cell>{selectedPerson['Club']}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Classement Général</strong></Table.Cell>
                <Table.Cell>{selectedPerson['Cl. Gal']} ème sur {lastRank}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Médaille</strong></Table.Cell>
                <Table.Cell>{selectedPerson['Méd.']}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Catégorie</strong></Table.Cell>
                <Table.Cell
                  className="text-danger fw-bold"
                  onClick={() => handleCategoryClick(selectedPerson['Catégorie'])}
                  style={{ cursor: 'pointer' }}
                >
                  {selectedPerson['Catégorie']}
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Meilleur Score</strong></Table.Cell>
                <Table.Cell>{selectedPerson['Season Best'] || selectedPerson['Score Best'] || '—'}</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>
      )}

      {/* === COMPARATEUR === */}
      {compareList.length > 0 && (
        <div ref={compareResultsRef} className="container mt-5">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="premium-section-title mb-0">
              Comparateur <span className="text-primary">({compareList.length}/5)</span>
            </h3>
            <button className="btn btn-outline-danger btn-sm" onClick={clearCompare}>
              Tout retirer
            </button>
          </div>

          <div className="table-responsive">
            <Table celled compact className="premium-table">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Critère</Table.HeaderCell>
                  {compareList.map((p, i) => (
                    <Table.HeaderCell key={i} className="text-center">
                      {p['Nom']} {p['Prénom']}
                      <button
                        className="btn btn-sm btn-link text-danger p-0 ms-2"
                        onClick={() => removeFromCompare(p)}
                        title="Retirer"
                      >
                        ✕
                      </button>
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <Table.Row>
                  <Table.Cell><strong>Club</strong></Table.Cell>
                  {compareList.map((p, i) => <Table.Cell key={i} className="text-center">{p['Club']}</Table.Cell>)}
                </Table.Row>
                <Table.Row>
                  <Table.Cell><strong>Naissance</strong></Table.Cell>
                  {compareList.map((p, i) => <Table.Cell key={i} className="text-center">{p['Naissance']}</Table.Cell>)}
                </Table.Row>
                <Table.Row>
                  <Table.Cell><strong>Classement Général</strong></Table.Cell>
                  {compareList.map((p, i) => (
                    <Table.Cell key={i} className="text-center">{p['Cl. Gal']} ème sur {lastRank}</Table.Cell>
                  ))}
                </Table.Row>
                <Table.Row>
                  <Table.Cell><strong>Médaille</strong></Table.Cell>
                  {compareList.map((p, i) => <Table.Cell key={i} className="text-center">{p['Méd.']}</Table.Cell>)}
                </Table.Row>
                <Table.Row>
                  <Table.Cell><strong>Catégorie</strong></Table.Cell>
                  {compareList.map((p, i) => (
                    <Table.Cell
                      key={i}
                      className="text-center text-danger fw-bold"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleCategoryClick(p['Catégorie'])}
                    >
                      {p['Catégorie']}
                    </Table.Cell>
                  ))}
                </Table.Row>
                <Table.Row>
                  <Table.Cell><strong>Meilleur Score</strong></Table.Cell>
                  {compareList.map((p, i) => (
                    <Table.Cell key={i} className="text-center">{p['Season Best'] || p['Score Best'] || '—'}</Table.Cell>
                  ))}
                </Table.Row>
              </Table.Body>
            </Table>
          </div>
        </div>
      )}

      {/* ====================== MODALE BOOTSTRAP ====================== */}
      {isModalOpen && selectedCategory && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: '1150px' }}>
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">
                  Membres de la catégorie : <strong>{selectedCategory}</strong>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsModalOpen(false)}
                  aria-label="Close"
                ></button>
              </div>

              <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                <table className="table table-bordered table-hover text-center align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Nom Prénom</th>
                      <th>Naissance</th>
                      <th>Place dans la catégorie</th>
                      <th>Classement général</th>
                      <th>Médaille</th>
                      <th>Club</th>
                      <th>Best Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMembers(getCategoryRanking(getCategoryMembers(selectedCategory))).map((m, i) => (
                      <tr key={i}>
                        <td>{m['Nom']} {m['Prénom']}</td>
                        <td>{m['Naissance']}</td>
                        <td><strong>{m.CategoryRank}</strong></td>
                        <td>{m['Cl. Gal']} ème sur {lastRank}</td>
                        <td>{m['Méd.']}</td>
                        <td>{m['Club']}</td>
                        <td>{m['Season Best'] || m['Score Best'] || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4 px-2">
                    <span className="text-muted">
                      Page <strong>{currentPage}</strong> sur {totalPages}
                    </span>

                    <div className="btn-group">
                      <button
                        className="btn btn-outline-secondary"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                      >
                        Précédent
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => p + 1)}
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Fermer
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchPage;
